import React, { useState } from 'react';
import { Clock, Check, X, Send, Loader2, Eye } from 'lucide-react';
import { signatureService } from '../services/signatureService';

/**
 * SignatureStatusTracker component displays the status of all signature requests for a document
 * 
 * @param {Object} props
 * @param {Array} props.requests - Array of signature requests for the document
 * @param {string} props.documentId - ID of the document
 * @param {Function} props.onRefresh - Callback when signatures are refreshed
 */
const SignatureStatusTracker = ({ requests = [], documentId, onRefresh }) => {
  const [loadingReminder, setLoadingReminder] = useState(null);
  const [reminderError, setReminderError] = useState(null);
  const [reminderSuccess, setReminderSuccess] = useState(null);
  
  // Send a reminder to a signer
  const sendReminder = async (requestId) => {
    try {
      setLoadingReminder(requestId);
      setReminderError(null);
      setReminderSuccess(null);
      
      await signatureService.sendSignatureReminder(requestId);
      
      setReminderSuccess(requestId);
      setTimeout(() => setReminderSuccess(null), 3000);
      
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error sending reminder:', error);
      setReminderError(requestId);
      setTimeout(() => setReminderError(null), 3000);
    } finally {
      setLoadingReminder(null);
    }
  };
  
  // Get status icon based on request status
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <Check className="text-green-500" size={18} />;
      case 'declined':
        return <X className="text-red-500" size={18} />;
      case 'viewed':
        return <Eye className="text-blue-500" size={18} />;
      case 'pending':
      default:
        return <Clock className="text-yellow-500" size={18} />;
    }
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };
  
  // Sort requests by order
  const sortedRequests = [...requests].sort((a, b) => {
    // First by order
    if (a.order !== b.order) {
      return a.order - b.order;
    }
    // Then by status (completed first, then viewed, then pending)
    const statusOrder = { completed: 1, declined: 2, viewed: 3, pending: 4 };
    return statusOrder[a.status] - statusOrder[b.status];
  });
  
  if (sortedRequests.length === 0) {
    return (
      <div className="text-center p-4 bg-gray-800 rounded-lg border border-gray-700">
        <p className="text-gray-400">No signature requests found for this document</p>
      </div>
    );
  }
  
  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
      <h3 className="text-lg font-semibold mb-4">Signature Status</h3>
      
      <div className="space-y-4">
        {sortedRequests.map((request) => (
          <div 
            key={request.requestId} 
            className="p-3 bg-gray-700 rounded-lg transition-colors"
          >
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center">
                {getStatusIcon(request.status)}
                <span className="ml-2 font-medium">{request.userName}</span>
                {request.order > 0 && (
                  <span className="ml-2 text-xs bg-gray-600 px-2 py-0.5 rounded">
                    Order: {request.order}
                  </span>
                )}
              </div>
              
              <div className="text-sm text-gray-400">
                {request.userEmail}
              </div>
            </div>
            
            <div className="flex flex-wrap justify-between items-center text-sm">
              <div className="space-y-1">
                <div className="flex items-center text-gray-400">
                  <span className="font-medium mr-2">Status:</span>
                  <span className={`capitalize ${
                    request.status === 'completed' ? 'text-green-500' : 
                    request.status === 'declined' ? 'text-red-500' : 
                    request.status === 'viewed' ? 'text-blue-500' : 
                    'text-yellow-500'
                  }`}>
                    {request.status}
                  </span>
                </div>
                
                {request.viewedAt && (
                  <div className="text-gray-400">
                    <span className="font-medium mr-2">Viewed:</span>
                    {formatDate(request.viewedAt)}
                  </div>
                )}
                
                {request.completedAt && (
                  <div className="text-gray-400">
                    <span className="font-medium mr-2">Completed:</span>
                    {formatDate(request.completedAt)}
                  </div>
                )}
                
                {request.declinedAt && (
                  <div>
                    <div className="text-gray-400">
                      <span className="font-medium mr-2">Declined:</span>
                      {formatDate(request.declinedAt)}
                    </div>
                    {request.declineReason && (
                      <div className="text-gray-400 mt-1">
                        <span className="font-medium mr-2">Reason:</span>
                        <span className="text-red-400">{request.declineReason}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {(request.status === 'pending' || request.status === 'viewed') && (
                <button
                  onClick={() => sendReminder(request.requestId)}
                  disabled={loadingReminder === request.requestId}
                  className={`flex items-center px-3 py-1.5 rounded-md ${
                    reminderSuccess === request.requestId ? 'bg-green-600' : 
                    reminderError === request.requestId ? 'bg-red-600' : 
                    'bg-blue-600 hover:bg-blue-500'
                  }`}
                >
                  {loadingReminder === request.requestId ? (
                    <Loader2 className="animate-spin mr-1" size={16} />
                  ) : reminderSuccess === request.requestId ? (
                    <Check className="mr-1" size={16} />
                  ) : reminderError === request.requestId ? (
                    <X className="mr-1" size={16} />
                  ) : (
                    <Send className="mr-1" size={16} />
                  )}
                  
                  {reminderSuccess === request.requestId ? 'Sent!' : 
                   reminderError === request.requestId ? 'Failed' : 
                   loadingReminder === request.requestId ? 'Sending...' : 
                   'Send Reminder'}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SignatureStatusTracker; 